/**
 * Единые дизайн-токены сервиса «Любить по-русски» — общий источник правды
 * для web (Tailwind/CSS-переменные) и mobile-app (React Native).
 *
 * Источник извлечения: web/styles/theme.css (:root, light), fonts.css,
 * index.css/app.css (медиа-брейкпоинты).
 *
 * Формат значений — plain числа/строки (никаких CSS-функций и объектов
 * платформы), чтобы значения импортировались и в браузер, и в RN как есть.
 * Длина задаётся в px (число) — потребитель сам решает, rem/px/dp.
 */

// ── Цвета ───────────────────────────────────────────────────────────────────

/** Бренд-градиент утверждён Ириной 17.08: 135°, #FB2C36 → #FE9A00. */
export const colors = {
  brandFrom: "#FB2C36",
  brandTo: "#FE9A00",
  /** var(--brand-cta-shadow) */
  brandCtaShadow: "rgba(251, 44, 54, 0.4)",
  /** var(--brand-surface-warm): тёплый нейтральный фон. */
  brandSurfaceWarm: "#F6F1EB",

  background: "#ffffff",
  foreground: "#252525", // var(--foreground): oklch(0.145 0 0) → sRGB
  card: "#ffffff",
  cardForeground: "#252525",
  primary: "#030213",
  primaryForeground: "#ffffff",
  secondary: "#f1f1f3", // var(--secondary): oklch(0.95 0.0058 264.53)
  secondaryForeground: "#030213",
  muted: "#ececf0",
  mutedForeground: "#717182",
  accent: "#e9ebef",
  accentForeground: "#030213",
  destructive: "#d4183d",
  destructiveForeground: "#ffffff",
  border: "rgba(0, 0, 0, 0.1)",
  inputBackground: "#f3f3f5",
} as const;

// ── Типографика ─────────────────────────────────────────────────────────────

export const fontFamily =
  "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif" as const;

/** Размеры в px, начертания — числовые веса (используются и в web, и в RN). */
export const typography = {
  baseFontSize: 16,
  weightNormal: 400,
  weightMedium: 500,
  weightSemibold: 600,
  weightBold: 700,
  /** Семантические стили (мобильная сетка, сверта с mobile-app/src/theme/tokens.ts). */
  title: { fontSize: 20, fontWeight: 700 },
  body: { fontSize: 15, fontWeight: 400 },
  label: { fontSize: 13, fontWeight: 500 },
} as const;

// ── Отступы ─────────────────────────────────────────────────────────────────

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

// ── Радиусы ─────────────────────────────────────────────────────────────────

/** var(--radius): 0.625rem ≈ 10px. */
export const radii = {
  sm: 8,
  md: 10,
  lg: 12,
  xl: 16,
  full: 9999,
} as const;

// ── Тени ────────────────────────────────────────────────────────────────────

/** Готовые box-shadow-строки (web). В RN их пересобирают по компонентам. */
export const shadows = {
  /** Свечение вокруг брендовых CTA. */
  brandCta: "0 4px 14px rgba(251, 44, 54, 0.4)",
} as const;

// ── Брейкпоинты ─────────────────────────────────────────────────────────────

/**
 * Медиа-брейкпоинты из web/styles/app.css (min-width: 420px / 640px).
 * plain числа в px; web сам оборачивает в @media, RN — в useWindowDimension.
 */
export const breakpoints = {
  sm: 420,
  md: 640,
} as const;


// ── Спека 2026-09-20 (claude-agent-system/knowledge/russian-love/mobile-spec/00-foundations.md) ──

/**
 * Поверхности и текст (app.css:45-52).
 * Бренд-цвета НЕ дублируются — см. colors.brandFrom/brandTo выше.
 */
export const surface = {
  /** var(--rla-bg): фон приложения. */
  bg: "#F6F1EB",
  /** var(--rla-surface): карточки, панели. */
  surface: "#ffffff",
  /** var(--rla-sunk): «утопленное» — поле ввода, счётчик, цитата. */
  sunk: "#f2ece5",
} as const;

export const ink = {
  /** var(--rla-ink): основной текст. */
  ink: "#171114",
  /** var(--rla-ink-2): вторичный текст. */
  ink2: "#6b6169",
  /** var(--rla-ink-3): третичный/приглушённый. */
  ink3: "#9a9098",
  /** var(--rla-hairline): волосок-разделитель. */
  hairline: "rgba(23, 17, 20, 0.07)",
} as const;

/** Смысловые цвета — НЕ бренд (app.css:43-91). */
export const semantic = {
  /** var(--rla-blue): супер-симпатия (градиент, from → to). */
  blueFrom: "#51a2ff",
  blueTo: "#155dfc",
  /** var(--rla-online) / var(--rla-ok): «в сети», «совпало». */
  ok: "#2fbf6b",
  /** var(--rla-alarm): деструктивное (холодный глухой красный — НЕ бренд-красный). */
  alarm: "#b3372f",
  alarmDeep: "#992c25",
  /** var(--rla-alarm-soft): подложка тревоги (from → to, 150deg). */
  alarmSoftFrom: "#fde7e4",
  alarmSoftTo: "#fbd6d3",
  /** var(--rla-warn): обратимое, но требующее подтверждения. */
  warn: "#c9740b",
  /** var(--rla-warn-soft): подложка предупреждения. */
  warnSoftFrom: "#fff0d2",
  warnSoftTo: "#ffe3b4",
  /** var(--rla-bubble-outline): контур своей реплики в чате. */
  bubbleOutline: "#fd7b0f",
  /** var(--rla-bubble-outline-w): 0.3px — на @2x браузер поднимает до 1 физического. */
  bubbleOutlineWidth: 0.3,
  /** var(--rla-alarm-shadow) (app.css:83). */
  alarmShadow: "rgba(179, 55, 47, 0.5)",
  /** var(--rla-warn-shadow) (app.css:92). */
  warnShadow: "rgba(201, 116, 11, 0.5)",
} as const;

/**
 * Скругления — жёсткая шкала 999 / 28 / 22 / 18 (app.css:95-97, :3586).
 * Любой другой радиус считается браком (см. спеку, §3).
 */
export const radiusScale = {
  /** Кнопки, пилюли. */
  pill: 999,
  /** var(--rla-r-card): карточка ленты, крупные поверхности. */
  card: 28,
  /** var(--rla-r-inner): вложенные блоки, поля ввода. */
  inner: 22,
  /** var(--rla-r-row): строка списка. */
  row: 18,
} as const;

/**
 * Глубина — пять ступеней (app.css:108-121) + вне шкалы (app.css:123-139).
 * Web: box-shadow-строки как есть. RN: пересобирается по платформе
 * (iOS shadow*, Android elevation) — деградацию см. в спеке §10.3.
 */
export const elevation = {
  /** var(--rla-e0): волосок — разделение без отрыва от фона. */
  e0: "inset 0 0 0 1px rgba(23, 17, 20, 0.07)",
  /** var(--rla-e1): панель — секция формы, список. */
  e1: "0 1px 2px rgba(23,17,20,.04), 0 10px 30px -18px rgba(23,17,20,.22)",
  /** var(--rla-e2): пилюля над фоном. */
  e2: "0 1px 2px rgba(23,17,20,.04), 0 8px 20px -14px rgba(23,17,20,.35)",
  /** var(--rla-e3): карточка ленты — главный объект экрана. */
  e3: "0 0 0 1px rgba(23,17,20,.06), 0 24px 48px -18px rgba(23,17,20,.45), 0 4px 14px rgba(23,17,20,.1)",
  /** var(--rla-e4): диалог поверх затемнения. */
  e4: "0 1px 2px rgba(23,17,20,.06), 0 30px 60px -24px rgba(23,17,20,.6)",
  /** var(--rla-shadow-brand): 0 10px 26px -10px var(--rla-cta-shadow) (app.css:125). */
  brand: "0 10px 26px -10px rgba(251, 44, 54, 0.4)",
  /** var(--rla-shadow-cta): бренд + чёрная ступень + внутренний блик (блик — часть рецепта, app.css:126-129). */
  cta:
    "0 12px 28px -8px rgba(251, 44, 54, 0.4), 0 3px 10px rgba(0, 0, 0, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.32)",
  /** var(--rla-shadow-glass): стекло — только поверх фотографий (app.css:130-133). */
  glass:
    "inset 0 0 0 1px rgba(255, 255, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.42), 0 6px 18px rgba(0, 0, 0, 0.22)",
  /** var(--rla-shadow-solid): плотная поверхность с внутренней рамкой (app.css:134-137). */
  solid:
    "inset 0 0 0 1.5px rgba(23, 17, 20, 0.12), 0 10px 24px -12px rgba(23, 17, 20, 0.4)",
} as const;

/**
 * Движение (app.css:141-165): основной easing, нажатие кнопки, рецепт
 * появления списка — единственный, второго не заводить (решение Ирины 27.08).
 */
export const motion = {
  /** var(--rla-ease-out) */
  easeOut: "cubic-bezier(0.23, 1, 0.32, 1)",
  /** Нажатие кнопки: scale 0.97 за 140ms. */
  pressScale: 0.97,
  pressMs: 140,
  /** Появление списка: сдвиг 8px, 240ms, разбежка 26ms, максимум 5 ступеней. */
  listDy: 8,
  listMs: 240,
  listStaggerMs: 26,
  listMaxSteps: 5,
} as const;

/**
 * Кнопка .rla-btn (app.css:3583-3606) и её размеры.
 * Веса по решению Ирины 20.09: 650→700, 750→800, 450→500 (в вебе 650 рисуется
 * как 700 — фикция переменного шрифта); шрифт статический Inter, вшит в бандл.
 */
export const button = {
  height: 48,
  heightSm: 40,
  paddingH: 22,
  paddingHSm: 16,
  text: { fontSize: 15, fontWeight: 700, letterSpacing: -0.15 },
  textSm: { fontSize: 13.5 },
  iconGap: 8,
  disabledOpacity: 0.5,
} as const;

/** Вшитые в бандл статические Inter (rn: assets/fonts; web не тянет CDN).
 *
 * ⏳ PENDING iOS-ПРОВЕРКА (решение Ирины 20.09): сборка сейчас только Android
 * (gradlew assembleDebug); подключение Inter на iOS (Info.plist UIAppFonts /
 * Podfile) и проверка рендера — вне скоупа до первой iOS-сборки. */
export const fontAssets = [
  { weight: 400, family: "Inter-Regular" },
  { weight: 500, family: "Inter-Medium" },
  { weight: 600, family: "Inter-SemiBold" },
  { weight: 700, family: "Inter-Bold" },
  { weight: 800, family: "Inter-ExtraBold" },
] as const;

/**
 * Начертание по весу. Android резолвит статический шрифт по ИМЕНИ ФАЙЛА
 * (assets/fonts/Inter-Bold.ttf → fontFamily "Inter-Bold") и fontWeight поверх
 * статического начертания не применяет. Поэтому в RN семейство выбирается
 * напрямую, а не через fontWeight: иначе экран молча рисуется системным Roboto
 * и расходится с вебом — вшитый в бандл Inter остаётся неиспользованным.
 *
 * Веса уже приведены по решению Ирины 20.09 (650→700, 750→800, 450→500).
 */
export const fontFamilyByWeight = {
  400: "Inter-Regular",
  500: "Inter-Medium",
  600: "Inter-SemiBold",
  700: "Inter-Bold",
  800: "Inter-ExtraBold",
} as const;

export type InterWeight = keyof typeof fontFamilyByWeight;

export const tokens = { colors, fontFamily, typography, spacing, radii, shadows, breakpoints, surface, ink, semantic, radiusScale, elevation, motion, button, fontAssets, fontFamilyByWeight } as const;
export type Tokens = typeof tokens;
